import {useEffect, useRef, useState} from "react";
import {subMonths} from "date-fns";
import ReportUtils from "../../../utils/report-utils.js";
import OccurrenceAssociatedVehiclesService from "../../../services/reports/occurrence-associated-vehicles-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";

const initialFilter = () => ({
    dtStart:  subMonths(new Date(), 3),
    dtEnd: new Date(),
    reason: [],
    monitoredVehicle: null,
    occurrenceStatus: null,
    vehicleClassif: [],
    cityFact: [],
    archivedOccurrences: true,
    onlyMyCity: true,
});

const filtersLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    reason: "Natureza",
    monitoredVehicle: "Veículo monitorado",
    occurrenceStatus: "Status da ocorrência",
    vehicleClassif: "Classificação dos veículos na ocorrência",
    cityFact: "Município do fato",
    archivedOccurrences: "Contabilizar ocorrências arquivadas",
    onlyMyCity: "Apenas ocorrências da minha corporação",
}

export default function OccurrenceAssociatedVehicles() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [headerSelected, setHeaderSelected] = useState(- 1);
    const [filters, setFilters] = useState(initialFilter);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Veículos associados a ocorrências";

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
            }, filtersLabels)
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 3, "months");

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            }

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
                reason: filters.reason?.map(i => i.value) ?? [],
                monitoredVehicle: filters.monitoredVehicle?.value ?? null,
                occurrenceStatus: filters.occurrenceStatus?.value ?? "",
                vehicleClassif: filters.vehicleClassif?.map(i => i.value) ?? [],
                cityFact: filters.cityFact?.map(i => i.value) ?? [],
                archivedOccurrences: filters.archivedOccurrences ?? false,
                onlyMyCity: filters.onlyMyCity ?? false,
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, formattedFilters, true);

            const response = await OccurrenceAssociatedVehiclesService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    }

    useGlobalKey("Enter", apply);

    const hasData =
        !!reportData &&
        (
            (reportData.totalVehicles?.[0]?.qty ?? 0) > 0 ||
            (reportData.totalOccurrences?.[0]?.vehicle_occ ?? 0) > 0 ||
            (reportData.totalOccurrencesWithMultiVehicles?.[0]?.multi_vehicle_occ ?? 0) > 0 ||
            (reportData.totalOccurrencesWithMultiReasons?.[0]?.multi_reasons_occ ?? 0) > 0 ||
            (reportData.vehicleClassification?.length ?? 0) > 0 ||
            (reportData.vehicleTypes?.length ?? 0) > 0 ||
            (reportData.vehicleTypesByReason?.length ?? 0) > 0 ||
            (reportData.vehicleBrandModel?.length ?? 0) > 0
        );

    const noDataValue = hasData ? 1 : 0;

    const cardClassName = [
        "w-full h-fit mr-2 print:!mt-[-15px] transform",
        !reportData || !hasData ? "mt-[59px] !bg-[#f3f3f4] !border-none !-translate-y-[5px]" : "mt-[61px]",
        collapsed ? "ml-2" : "",
    ].join(" ");

    const cardBodyClassName = [
        "flex-1",
        !reportData || !hasData ? "p-[0]" : "px-[20px] py-[5px]",
    ].join(" ");

    return (
        <div className="flex">
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>{title}</Template.Title>
                    <HeaderButtons
                        exportable={true}
                        exportInSheets={true}
                        printable={true}
                        headerChange={true}
                        printComponentRef={printComponentRef}
                        reportData={reportData}
                        data={noDataValue}
                        headerSelected={headerSelected}
                        setHeaderSelected={setHeaderSelected}
                    />
                </Template.Header>
                <Template.Body className={`${collapsed ? "p-[0]" : ""}`}>
                    <Menu
                        buttonRef={buttonRef}
                        fixedTop={fixedTop}
                        collapsed={collapsed}
                        toggleFilters={toggleFilters}
                        clearSearch={clearSearch}
                        apply={apply}
                    >
                        <Filters filters={filters} setFilters={setFilters} />
                        <AutoDatetime setFilters={setFilters} />
                    </Menu>
                </Template.Body>
            </Template>
            <Card className={cardClassName}>
                <Card.Body className={cardBodyClassName}>
                    <NotAppliedContent reportData={reportData} />
                    {reportData && !hasData && (
                        <NoDataContent reportData={reportData} data={noDataValue} />
                    )}

                    {reportData && hasData && (
                        <Content
                            reportData={reportData}
                            data={hasData}
                            entity={headerSelected}
                            title={title}
                            collapsed={collapsed}
                            printComponentRef={printComponentRef}
                            setIsLoading={setIsLoading}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    )
}